import { Platform } from 'react-native';

import { applePlatformFlags } from '@/config/applePlatformFlags';
import { isAppleIntent, logAppleIntentResult, logSpotlightOpen } from '@/services/appleIntentsAndSpotlight';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

export type AppleDeepLinkAuthState = 'signed_out' | 'signed_in_not_onboarded' | 'signed_in_onboarded';

const APPLE_SURFACE_DEDUP_MS = 4000;
const recentAppleSurfaceKeys = new Map<string, number>();

export const queryStringParam = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (Array.isArray(value) && typeof value[0] === 'string') {
    const trimmed = value[0].trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  return undefined;
};

const shouldDedupe = (key: string): boolean => {
  const now = Date.now();
  const lastSeen = recentAppleSurfaceKeys.get(key);
  if (lastSeen != null && now - lastSeen < APPLE_SURFACE_DEDUP_MS) {
    return true;
  }
  recentAppleSurfaceKeys.set(key, now);
  return false;
};

/**
 * Logs Spotlight + read-only App Intent analytics from URL/query params.
 * Safe to call from both Linking listeners and Expo Router search params.
 */
export const processAppleSurfaceQueryParams = (
  queryParams: Record<string, unknown> | undefined,
): void => {
  if (!applePlatformFlags.phase5Enabled || !queryParams) {
    return;
  }

  if (applePlatformFlags.spotlightEnabled) {
    const spotlightType = queryStringParam(queryParams.spotlightType);
    const spotlightEntityId = queryStringParam(queryParams.spotlightEntityId);
    if ((spotlightType === 'trip' || spotlightType === 'template') && spotlightEntityId) {
      const dedupeKey = `spotlight:${spotlightType}:${spotlightEntityId}`;
      if (!shouldDedupe(dedupeKey)) {
        void logSpotlightOpen(spotlightType, spotlightEntityId);
      }
    }
  }

  if (applePlatformFlags.appIntentsReadActionsEnabled) {
    const appIntent = queryStringParam(queryParams.appIntent);
    if (isAppleIntent(appIntent)) {
      const dedupeKey = `intent:${appIntent}`;
      if (!shouldDedupe(dedupeKey)) {
        void logAppleIntentResult(appIntent, 'success');
      }
    }
  }
};

export const logTemplateDeepLinkAnalytics = (opts: {
  topic?: string;
  templateId?: string;
  ref?: string;
  utmSource?: string;
  authState: AppleDeepLinkAuthState;
}): void => {
  const baseKey = [
    opts.topic ?? '',
    opts.templateId ?? '',
    opts.ref ?? '',
    opts.utmSource ?? '',
    opts.authState,
  ].join(':');

  if (!shouldDedupe(`template_deeplink_opened:${baseKey}`)) {
    void logAnalyticsEvent('template_deeplink_opened', {
      topicTag: opts.topic,
      templateId: opts.templateId,
      ref: opts.ref,
      utmSource: opts.utmSource,
      authState: opts.authState,
      platform: Platform.OS,
    });
  }

  if (!shouldDedupe(`apple_universal_link_opened:${baseKey}`)) {
    void logAnalyticsEvent('apple_universal_link_opened', {
      route: '/templates',
      ref: opts.ref,
      utmSource: opts.utmSource,
      authState: opts.authState,
    });
  }
};
